import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { Button, CloseIcon, MutedCameraIcon, PlusIcon } from '@streamr/tv-ui';
import { useCallback, useEffect, useRef, useState, } from 'react';
import { createPortal } from 'react-dom';
import styled from 'styled-components';
import { BroadcastStateService } from '../services/broadcaststate/BroadcastStateService';
import { generateVideoSlotId, } from '../services/scene/Scene';
import { SceneService } from '../services/scene/SceneService';
export const FILMSTRIP_PORTAL_ID = 'filmstrip-portal-target';
import { FILM_STRIP_SOURCE_MIME, FilmStrip, } from './FilmStrip';
function useIsFullscreen() {
    const [isFullscreen, setIsFullscreen] = useState(() => !!document.fullscreenElement);
    useEffect(() => {
        const onChange = () => setIsFullscreen(!!(document.fullscreenElement ||
            document.webkitFullscreenElement));
        document.addEventListener('fullscreenchange', onChange);
        document.addEventListener('webkitfullscreenchange', onChange);
        return () => {
            document.removeEventListener('fullscreenchange', onChange);
            document.removeEventListener('webkitfullscreenchange', onChange);
        };
    }, []);
    return isFullscreen;
}
const MIN_SLOT_SIZE = 0.1;
const HANDLE_SIZE = 10;
function clamp(value, min, max) {
    return Math.min(max, Math.max(min, value));
}
function applyMove(slot, dxNorm, dyNorm) {
    const x = clamp(slot.x + dxNorm, 0, 1 - slot.width);
    const y = clamp(slot.y + dyNorm, 0, 1 - slot.height);
    return { ...slot, x, y };
}
function applyResize(slot, dxNorm, dyNorm, corner) {
    let { x, y, width, height } = slot;
    if (corner === 'se') {
        width = clamp(slot.width + dxNorm, MIN_SLOT_SIZE, 1 - slot.x);
        height = clamp(slot.height + dyNorm, MIN_SLOT_SIZE, 1 - slot.y);
    }
    else if (corner === 'sw') {
        const newWidth = clamp(slot.width - dxNorm, MIN_SLOT_SIZE, slot.x + slot.width);
        x = slot.x + slot.width - newWidth;
        width = newWidth;
        height = clamp(slot.height + dyNorm, MIN_SLOT_SIZE, 1 - slot.y);
    }
    else if (corner === 'ne') {
        width = clamp(slot.width + dxNorm, MIN_SLOT_SIZE, 1 - slot.x);
        const newHeight = clamp(slot.height - dyNorm, MIN_SLOT_SIZE, slot.y + slot.height);
        y = slot.y + slot.height - newHeight;
        height = newHeight;
    }
    else if (corner === 'nw') {
        const newWidth = clamp(slot.width - dxNorm, MIN_SLOT_SIZE, slot.x + slot.width);
        x = slot.x + slot.width - newWidth;
        width = newWidth;
        const newHeight = clamp(slot.height - dyNorm, MIN_SLOT_SIZE, slot.y + slot.height);
        y = slot.y + slot.height - newHeight;
        height = newHeight;
    }
    return { ...slot, x, y, width, height };
}
function applySlotStyle(el, slot) {
    el.style.left = `${slot.x * 100}%`;
    el.style.top = `${slot.y * 100}%`;
    el.style.width = `${slot.width * 100}%`;
    el.style.height = `${slot.height * 100}%`;
}
function SlotBackgroundIcon({ opaque }) {
    return (_jsxs("svg", { width: "20", height: "20", viewBox: "0 0 20 20", fill: "none", xmlns: "http://www.w3.org/2000/svg", children: [_jsx("title", { children: opaque ? 'Opaque background' : 'Transparent background' }), opaque ? (_jsx("rect", { x: "3", y: "3", width: "14", height: "14", rx: "3", fill: "white" })) : (_jsxs(_Fragment, { children: [_jsx("rect", { x: "3.75", y: "3.75", width: "12.5", height: "12.5", rx: "2.5", stroke: "white", strokeWidth: "1.5" }), _jsx("path", { d: "M16.25 8 L8 16.25 H13.75 A2.5 2.5 0 0 0 16.25 13.75 Z", fill: "white" })] }))] }));
}
function SlotOverlay({ slot, isSelected, onDragStart, onRemove, onClear, onToggleBackground, onClick, }) {
    const didDragRef = useRef(false);
    const savedPointerEvent = useRef(null);
    const corners = ['nw', 'ne', 'sw', 'se'];
    return (_jsxs(SlotBox, { "data-slot-id": slot.id, "$selected": isSelected, style: {
            left: `${slot.x * 100}%`,
            top: `${slot.y * 100}%`,
            width: `${slot.width * 100}%`,
            height: `${slot.height * 100}%`,
            zIndex: slot.zIndex ?? 0,
        }, onPointerDown: (e) => {
            e.stopPropagation();
            e.preventDefault();
            didDragRef.current = false;
            savedPointerEvent.current = e;
            const startX = e.clientX;
            const startY = e.clientY;
            const DRAG_THRESHOLD = 4;
            const onMoveCheck = (ev) => {
                if (!didDragRef.current &&
                    (Math.abs(ev.clientX - startX) > DRAG_THRESHOLD ||
                        Math.abs(ev.clientY - startY) > DRAG_THRESHOLD)) {
                    didDragRef.current = true;
                    if (savedPointerEvent.current) {
                        onDragStart(savedPointerEvent.current, slot.id, 'move');
                    }
                }
            };
            const onUpCheck = () => {
                document.removeEventListener('pointermove', onMoveCheck);
                document.removeEventListener('pointerup', onUpCheck);
                savedPointerEvent.current = null;
                if (!didDragRef.current) {
                    onClick(slot.id);
                }
            };
            document.addEventListener('pointermove', onMoveCheck);
            document.addEventListener('pointerup', onUpCheck);
        }, children: [_jsx(RemoveSlotButton, { type: "button", kind: "pill", title: "Delete slot", onPointerDown: (e) => {
                    // Keep SlotBox's click/drag tracking out of button
                    // presses — it would toggle the slot selection and can
                    // close the whole editor.
                    e.stopPropagation();
                }, onClick: (e) => {
                    e.stopPropagation();
                    onRemove(slot.id);
                }, children: _jsx(CloseIcon, {}) }), slot.sourceId !== undefined && (_jsx(ClearSlotButton, { type: "button", kind: "pill", title: "Clear slot", onPointerDown: (e) => {
                    e.stopPropagation();
                }, onClick: (e) => {
                    e.stopPropagation();
                    onClear(slot.id);
                }, children: _jsx(MutedCameraIcon, {}) })), _jsx(ToggleSlotBackgroundButton, { type: "button", kind: "pill", title: "Toggle slot background", onPointerDown: (e) => {
                    e.stopPropagation();
                }, onClick: (e) => {
                    e.stopPropagation();
                    onToggleBackground(slot.id);
                }, children: _jsx(SlotBackgroundIcon, { opaque: !slot.transparentBackground }) }), corners.map((corner) => (_jsx(ResizeHandle, { "$corner": corner, onPointerDown: (e) => {
                    e.stopPropagation();
                    e.preventDefault();
                    onDragStart(e, slot.id, 'resize', corner);
                } }, corner)))] }));
}
export function VideoSlotEditor({ hudVisible }) {
    const displayScene = SceneService.getOutput().useDisplayScene();
    const activeBroadcast = BroadcastStateService.getOutput().useRequiredActiveBroadcast();
    const containerRef = useRef(null);
    const filmStripOverlayRef = useRef(null);
    const filmStripContainerRef = useRef(null);
    const [editorVisible, setEditorVisible] = useState(false);
    const [selectedSlotId, setSelectedSlotId] = useState(undefined);
    const dragRef = useRef(null);
    const dragResultRef = useRef(null);
    const displaySceneRef = useRef(displayScene);
    displaySceneRef.current = displayScene;
    const lastSelectedSlotIdRef = useRef(undefined);
    const isHost = activeBroadcast.myRole === 'broadcaster';
    const handleSlotClick = useCallback((slotId) => {
        const prev = lastSelectedSlotIdRef.current;
        if (prev === slotId) {
            setEditorVisible(false);
            setSelectedSlotId(undefined);
            lastSelectedSlotIdRef.current = undefined;
        }
        else {
            setSelectedSlotId(slotId);
            lastSelectedSlotIdRef.current = slotId;
        }
        const scene = displaySceneRef.current;
        if (!scene)
            return;
        const slot = scene.videoSlots.find((s) => s.id === slotId);
        if (!slot)
            return;
        const maxZ = Math.max(...scene.videoSlots.map((s) => s.zIndex ?? 0));
        const slotZ = slot.zIndex ?? 0;
        const isAlreadyOnTop = slotZ === maxZ &&
            scene.videoSlots.filter((s) => (s.zIndex ?? 0) === maxZ).length ===
                1;
        if (!isAlreadyOnTop && scene.videoSlots.length > 1) {
            SceneService.getNotificationInterface().notifyUpdateScene({
                ...scene,
                videoSlots: scene.videoSlots.map((s) => s.id === slotId ? { ...s, zIndex: maxZ + 1 } : s),
            });
        }
    }, []);
    const handleEditorAreaClick = useCallback(() => {
        if (!editorVisible) {
            setEditorVisible(true);
        }
        setSelectedSlotId(undefined);
        lastSelectedSlotIdRef.current = undefined;
    }, [editorVisible]);
    const handleFilmStripParticipantClick = useCallback((participant) => {
        const scene = displaySceneRef.current;
        if (!scene)
            return;
        const targetSlotId = selectedSlotId ?? lastSelectedSlotIdRef.current;
        if (!targetSlotId) {
            SceneService.getNotificationInterface().notifyUpdateScene({
                ...scene,
                backgroundSourceId: participant.id,
                backgroundColor: undefined,
            });
            return;
        }
        if (scene.videoSlots.length === 0)
            return;
        const slotExists = scene.videoSlots.some((s) => s.id === targetSlotId);
        const finalSlotId = slotExists
            ? targetSlotId
            : scene.videoSlots[0].id;
        SceneService.getNotificationInterface().notifyAssignSlotSource(scene.id, finalSlotId, participant.id);
    }, [selectedSlotId]);
    const handlePointerDown = useCallback((e, slotId, type, corner) => {
        const scene = displaySceneRef.current;
        if (!scene || !containerRef.current)
            return;
        const slot = scene.videoSlots.find((s) => s.id === slotId);
        if (!slot)
            return;
        const slotEl = containerRef.current.querySelector(`[data-slot-id="${slotId}"]`);
        if (!slotEl)
            return;
        const base = {
            slotId,
            startPointerX: e.clientX,
            startPointerY: e.clientY,
            startSlot: { ...slot },
            element: slotEl,
        };
        dragRef.current =
            type === 'resize' && corner
                ? { ...base, type, corner }
                : { ...base, type: 'move' };
        dragResultRef.current = null;
        const onMove = (ev) => {
            const drag = dragRef.current;
            if (!drag || !containerRef.current)
                return;
            const rect = containerRef.current.getBoundingClientRect();
            if (rect.width === 0 || rect.height === 0)
                return;
            const dxNorm = (ev.clientX - drag.startPointerX) / rect.width;
            const dyNorm = (ev.clientY - drag.startPointerY) / rect.height;
            const updated = drag.type === 'move'
                ? applyMove(drag.startSlot, dxNorm, dyNorm)
                : applyResize(drag.startSlot, dxNorm, dyNorm, drag.corner);
            dragResultRef.current = updated;
            applySlotStyle(drag.element, updated);
        };
        const onUp = () => {
            document.removeEventListener('pointermove', onMove);
            document.removeEventListener('pointerup', onUp);
            const scene = displaySceneRef.current;
            const result = dragResultRef.current;
            dragRef.current = null;
            dragResultRef.current = null;
            if (result && scene) {
                const updatedScene = {
                    ...scene,
                    videoSlots: scene.videoSlots.map((s) => s.id === result.id ? result : s),
                };
                SceneService.getNotificationInterface().notifyUpdateScene(updatedScene);
            }
        };
        document.addEventListener('pointermove', onMove);
        document.addEventListener('pointerup', onUp);
    }, []);
    const handleRemoveSlot = useCallback((slotId) => {
        const scene = displaySceneRef.current;
        if (!scene)
            return;
        SceneService.getNotificationInterface().notifyRemoveVideoSlot(scene.id, slotId);
        setSelectedSlotId((prev) => (prev === slotId ? undefined : prev));
    }, []);
    const handleClearSlot = useCallback((slotId) => {
        const scene = displaySceneRef.current;
        if (!scene)
            return;
        SceneService.getNotificationInterface().notifyAssignSlotSource(scene.id, slotId, undefined);
    }, []);
    const handleDragOver = useCallback((e) => {
        if (e.dataTransfer.types.includes(FILM_STRIP_SOURCE_MIME)) {
            e.preventDefault();
            e.dataTransfer.dropEffect = 'copy';
        }
    }, []);
    const handleDrop = useCallback((e) => {
        const sourceId = e.dataTransfer.getData(FILM_STRIP_SOURCE_MIME);
        if (!sourceId)
            return;
        e.preventDefault();
        const scene = displaySceneRef.current;
        if (!scene)
            return;
        const rect = e.currentTarget.getBoundingClientRect();
        if (rect.width === 0 || rect.height === 0)
            return;
        // Center a default-sized slot on the drop point, clamped to the
        // stage, and put it on top of the existing slots.
        const width = 0.5;
        const height = 0.5;
        const x = clamp((e.clientX - rect.left) / rect.width - width / 2, 0, 1 - width);
        const y = clamp((e.clientY - rect.top) / rect.height - height / 2, 0, 1 - height);
        const maxZ = scene.videoSlots.length
            ? Math.max(...scene.videoSlots.map((s) => s.zIndex ?? 0))
            : 0;
        const newSlotId = generateVideoSlotId();
        SceneService.getNotificationInterface().notifyAddVideoSlot(scene.id, {
            id: newSlotId,
            x,
            y,
            width,
            height,
            sourceId,
            zIndex: maxZ + 1,
        });
        setEditorVisible(true);
        setSelectedSlotId(newSlotId);
        lastSelectedSlotIdRef.current = newSlotId;
    }, []);
    const handleToggleSlotBackground = useCallback((slotId) => {
        const scene = displaySceneRef.current;
        if (!scene)
            return;
        SceneService.getNotificationInterface().notifyUpdateScene({
            ...scene,
            videoSlots: scene.videoSlots.map((s) => s.id === slotId
                ? { ...s, transparentBackground: !s.transparentBackground }
                : s),
        });
    }, []);
    const showSlotEditor = isHost && displayScene && displayScene.layoutId === 'manual';
    const slots = displayScene?.videoSlots ?? [];
    const selectedSlot = selectedSlotId
        ? slots.find((s) => s.id === selectedSlotId)
        : undefined;
    const isBackgroundTarget = editorVisible && !selectedSlotId;
    const highlightedSource = editorVisible
        ? selectedSlotId
            ? selectedSlot?.sourceId
            : displayScene?.backgroundSourceId
        : undefined;
    const isFullscreen = useIsFullscreen();
    const [filmStripPortal, setFilmStripPortal] = useState(null);
    useEffect(() => {
        setFilmStripPortal(document.getElementById(FILMSTRIP_PORTAL_ID));
    }, []);
    useEffect(() => {
        if (!showSlotEditor)
            return;
        const handleOutsideClick = (e) => {
            const target = e.target;
            if (containerRef.current?.contains(target))
                return;
            if (filmStripOverlayRef.current?.contains(target))
                return;
            if (filmStripContainerRef.current?.contains(target))
                return;
            setEditorVisible(false);
            setSelectedSlotId(undefined);
        };
        document.addEventListener('pointerdown', handleOutsideClick);
        return () => document.removeEventListener('pointerdown', handleOutsideClick);
    }, [showSlotEditor]);
    return (_jsxs(_Fragment, { children: [_jsxs(EditorRoot, { children: [showSlotEditor && (_jsxs(SlotContainer, { ref: containerRef, "data-automation-id": "slot-editor-area", "$visible": editorVisible, "$backgroundTarget": isBackgroundTarget, onClick: (e) => {
                            if (e.target === e.currentTarget) {
                                handleEditorAreaClick();
                            }
                        }, onDragOver: handleDragOver, onDrop: handleDrop, children: [editorVisible &&
                                slots.map((slot) => (_jsx(SlotOverlay, { slot: slot, isSelected: slot.id === selectedSlotId, onDragStart: handlePointerDown, onRemove: handleRemoveSlot, onClear: handleClearSlot, onToggleBackground: handleToggleSlotBackground, onClick: handleSlotClick }, slot.id))), editorVisible && (_jsx(AddSlotButton, { type: "button", kind: "secondary", title: "Add slot", onClick: () => {
                                    SceneService.getNotificationInterface().notifyAddVideoSlot(displayScene.id);
                                }, children: _jsx(PlusIcon, {}) })), editorVisible &&
                                (displayScene.backgroundSourceId !== undefined ||
                                    displayScene.backgroundColor !== undefined) && (_jsx(ClearBackgroundButton, { type: "button", kind: "secondary", title: "Clear background", onClick: () => {
                                    SceneService.getNotificationInterface().notifyUpdateScene({
                                        ...displayScene,
                                        backgroundSourceId: undefined,
                                        backgroundColor: undefined,
                                    });
                                }, children: _jsx(MutedCameraIcon, {}) }))] })), isFullscreen && hudVisible && showSlotEditor && (_jsx(FullscreenFilmStripOverlay, { ref: filmStripOverlayRef, children: _jsx(FilmStrip, { onParticipantClick: handleFilmStripParticipantClick, highlightedSourceId: highlightedSource }) }))] }), !isFullscreen &&
                displayScene?.layoutId === 'manual' &&
                filmStripPortal &&
                createPortal(_jsx(FilmStripContainer, { ref: filmStripContainerRef, children: _jsx(FilmStrip, { onParticipantClick: handleFilmStripParticipantClick, highlightedSourceId: highlightedSource }) }), filmStripPortal)] }));
}
const EditorRoot = styled.div `
    position: absolute;
    inset: 0;
    z-index: 5;
    pointer-events: none;
`;
const FullscreenFilmStripOverlay = styled.div `
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    pointer-events: auto;
    background: rgba(0, 0, 0, 0.6);
    backdrop-filter: blur(8px);
    padding: 0.5rem 0.75rem;
    display: flex;
    justify-content: center;
`;
const SlotContainer = styled.div `
    position: absolute;
    inset: 0;
    pointer-events: auto;
    border-radius: 4px;
    box-sizing: border-box;
    transition: outline-color 150ms ease;
    outline: 2px solid
        ${({ $visible, $backgroundTarget }) => {
    if (!$visible)
        return 'transparent';
    if ($backgroundTarget)
        return 'rgba(160, 180, 255, 0.9)';
    return 'rgba(123, 97, 255, 0.4)';
}};
    outline-offset: -2px;
`;
const FilmStripContainer = styled.div `
    pointer-events: auto;
    padding: 0.5rem 0.75rem;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 0.375rem;
`;
const SlotBox = styled.div `
    position: absolute;
    border: 2px solid
        ${({ $selected }) => $selected ? 'rgba(160, 180, 255, 0.9)' : 'rgba(123, 97, 255, 0.5)'};
    background: ${({ $selected }) => $selected ? 'rgba(160, 180, 255, 0.15)' : 'rgba(123, 97, 255, 0.08)'};
    border-radius: 4px;
    cursor: move;
    pointer-events: auto;
    box-sizing: border-box;

    &:hover {
        background: rgba(160, 180, 255, 0.15);
    }
`;
const handlePosition = {
    nw: `top: -${HANDLE_SIZE / 2}px; left: -${HANDLE_SIZE / 2}px; cursor: nwse-resize;`,
    ne: `top: -${HANDLE_SIZE / 2}px; right: -${HANDLE_SIZE / 2}px; cursor: nesw-resize;`,
    sw: `bottom: -${HANDLE_SIZE / 2}px; left: -${HANDLE_SIZE / 2}px; cursor: nesw-resize;`,
    se: `bottom: -${HANDLE_SIZE / 2}px; right: -${HANDLE_SIZE / 2}px; cursor: nwse-resize;`,
};
const ResizeHandle = styled.div `
    position: absolute;
    width: ${HANDLE_SIZE}px;
    height: ${HANDLE_SIZE}px;
    background: rgba(123, 97, 255, 1);
    border-radius: 2px;
    pointer-events: auto;
    ${({ $corner }) => handlePosition[$corner]}
`;
const RemoveSlotButton = styled(Button) `
    --Button_Height: 1.25rem;
    --Button_Width: 1.25rem;
    --Button_Background: rgba(255, 80, 80, 0.95);
    --Button_HoverBackground: rgba(255, 60, 60, 1);
    --Button_BorderRadius: 50%;
    position: absolute;
    top: 0.25rem;
    left: 0.25rem;
    pointer-events: auto;
    color: white;
    z-index: 1;
    opacity: 0;
    transition: opacity 150ms ease;

    svg {
        transform: scale(0.55);
    }

    ${SlotBox}:hover & {
        opacity: 1;
    }
`;
const ClearSlotButton = styled(RemoveSlotButton) `
    --Button_Background: rgba(80, 80, 80, 0.95);
    --Button_HoverBackground: rgba(60, 60, 60, 1);
    left: 1.75rem;
`;
const AddSlotButton = styled(Button) `
    --Button_Width: 2.5rem;
    --Button_Height: 2.5rem;
    --Button_Background: rgba(57, 56, 56, 0.9);
    --Button_HoverBackground: rgba(57, 56, 56, 1);
    --Button_BorderRadius: 0.75rem;
    position: absolute;
    top: 0.75rem;
    right: 0.75rem;
    pointer-events: auto;
    backdrop-filter: blur(10px);
    border: 1px solid rgba(255, 255, 255, 0.2);
    color: white;
`;
const ClearBackgroundButton = styled(AddSlotButton) `
    left: 0.75rem;
    right: auto;
`;
const ToggleSlotBackgroundButton = styled(ClearSlotButton) `
    left: 3.25rem;
`;
